import type {CollectionFieldSchema} from "typesense/lib/Typesense/Collection";
import type {CollectionCreateSchema} from "typesense/lib/Typesense/Collections";

import {
    organizationsSchema,
    partsSchema,
    partsVectorizedSchema,
    projectsSchema,
    userEmailsSchema,
    usersSchema,
} from "./schemas";

const schemasByCollection = {
    [projectsSchema.name]: projectsSchema,
    [organizationsSchema.name]: organizationsSchema,
    [usersSchema.name]: usersSchema,
    [userEmailsSchema.name]: userEmailsSchema,
    [partsSchema.name]: partsSchema,
    [partsVectorizedSchema.name]: partsVectorizedSchema,
} as const satisfies Record<string, CollectionCreateSchema>;

export type LegacyTypesenseParityCollection = Extract<keyof typeof schemasByCollection, string>;
export type TypesenseParityDocument = Record<string, unknown>;

export interface TypesenseFieldDifference {
    path: string;
    cloudPresent: boolean;
    internalPresent: boolean;
    cloudValue: unknown;
    internalValue: unknown;
}

export interface TypesenseRequiredFieldFailure {
    field: string;
    expectedType: string;
    actualType: string;
    value: unknown;
}

export interface AcceptedRepresentationDifference {
    path: string;
    policy: string;
}

export interface LegacyTypesenseDocumentParity {
    fieldDifferences: TypesenseFieldDifference[];
    acceptedRepresentationDifferences: AcceptedRepresentationDifference[];
    requiredFieldFailures: {
        cloud: TypesenseRequiredFieldFailure[];
        internal: TypesenseRequiredFieldFailure[];
    };
    partsMpn?: {
        hasMpnMismatch: boolean;
        mpnFieldPresenceMismatch: boolean;
        mpnValueMismatch: boolean;
    };
    equal: boolean;
}

export const LEGACY_TYPESENSE_PARITY_COLLECTIONS = Object.freeze(
    Object.keys(schemasByCollection) as LegacyTypesenseParityCollection[],
);

export const LEGACY_TYPESENSE_ACCEPTED_REPRESENTATION_POLICIES = Object.freeze({
    [partsSchema.name]: {
        path: "properties[].unit",
        policy: "Treat a null property unit as absent.",
    },
    [partsVectorizedSchema.name]: {
        path: "properties[].unit",
        policy: "Treat a null property unit as absent.",
    },
});

function hasOwn(value: TypesenseParityDocument, key: string): boolean {
    return Object.hasOwn(value, key);
}

function isRecord(value: unknown): value is TypesenseParityDocument {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function equal(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) {
        return true;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }
        return left.every(
            (value, index) => Object.hasOwn(left, index) === Object.hasOwn(right, index) && equal(value, right[index]),
        );
    }
    if (isRecord(left) && isRecord(right)) {
        const leftKeys = Object.keys(left).sort();
        const rightKeys = Object.keys(right).sort();
        return (
            leftKeys.length === rightKeys.length &&
            leftKeys.every((key, index) => key === rightKeys[index] && equal(left[key], right[key]))
        );
    }
    return false;
}

function differences(
    cloud: TypesenseParityDocument,
    internal: TypesenseParityDocument,
    prefix = "",
): TypesenseFieldDifference[] {
    const result: TypesenseFieldDifference[] = [];
    const keys = [...new Set([...Object.keys(cloud), ...Object.keys(internal)])].sort();
    for (const key of keys) {
        const path = prefix ? `${prefix}.${key}` : key;
        const cloudPresent = hasOwn(cloud, key);
        const internalPresent = hasOwn(internal, key);
        const cloudValue = cloud[key];
        const internalValue = internal[key];
        if (cloudPresent && internalPresent && isRecord(cloudValue) && isRecord(internalValue)) {
            result.push(...differences(cloudValue, internalValue, path));
        } else if (!cloudPresent || !internalPresent || !equal(cloudValue, internalValue)) {
            result.push({path, cloudPresent, internalPresent, cloudValue, internalValue});
        }
    }
    return result;
}

function normalizePropertyUnits(
    cloud: TypesenseParityDocument,
    internal: TypesenseParityDocument,
): {
    cloud: TypesenseParityDocument;
    internal: TypesenseParityDocument;
    accepted: AcceptedRepresentationDifference[];
} {
    const cloudProperties = Array.isArray(cloud["properties"]) ? cloud["properties"] : [];
    const internalProperties = Array.isArray(internal["properties"]) ? internal["properties"] : [];
    const accepted: AcceptedRepresentationDifference[] = [];
    const length = Math.max(cloudProperties.length, internalProperties.length);
    for (let index = 0; index < length; index += 1) {
        const cloudProperty = cloudProperties[index];
        const internalProperty = internalProperties[index];
        if (!isRecord(cloudProperty) || !isRecord(internalProperty)) {
            continue;
        }
        const cloudHasUnit = hasOwn(cloudProperty, "unit");
        const internalHasUnit = hasOwn(internalProperty, "unit");
        if (
            (cloudHasUnit && cloudProperty["unit"] === null && !internalHasUnit) ||
            (internalHasUnit && internalProperty["unit"] === null && !cloudHasUnit)
        ) {
            accepted.push({
                path: `properties[${index}].unit`,
                policy: "Treat a null property unit as absent.",
            });
        }
    }
    return {
        cloud: withoutNullPropertyUnits(cloud),
        internal: withoutNullPropertyUnits(internal),
        accepted,
    };
}

function withoutNullPropertyUnits(document: TypesenseParityDocument): TypesenseParityDocument {
    const properties = document["properties"];
    if (!Array.isArray(properties)) {
        return document;
    }
    let changed = false;
    const normalizedProperties = properties.map((property) => {
        if (!isRecord(property) || !hasOwn(property, "unit") || property["unit"] !== null) {
            return property;
        }
        changed = true;
        const normalizedProperty = {...property};
        delete normalizedProperty["unit"];
        return normalizedProperty;
    });
    return changed ? {...document, properties: normalizedProperties} : document;
}

function actualType(value: unknown): string {
    if (Array.isArray(value)) {
        return "array";
    }
    if (value === null) {
        return "null";
    }
    return typeof value;
}

function matchesType(value: unknown, type: CollectionFieldSchema["type"]): boolean {
    const arrayType = type.endsWith("[]");
    if (arrayType) {
        if (!Array.isArray(value)) {
            return false;
        }
        const itemType = type.slice(0, -2);
        return value.every((item) => matchesType(item, itemType as CollectionFieldSchema["type"]));
    }
    switch (type) {
        case "string":
        case "string*":
        case "image":
            return typeof value === "string";
        case "int32":
            return (
                typeof value === "number" &&
                Number.isInteger(value) &&
                value >= -2_147_483_648 &&
                value <= 2_147_483_647
            );
        case "int64":
            return typeof value === "number" && Number.isSafeInteger(value);
        case "float":
            return typeof value === "number" && Number.isFinite(value);
        case "bool":
            return typeof value === "boolean";
        case "object":
            return isRecord(value);
        case "auto":
            return value !== undefined;
        case "geopoint":
        case "geopolygon":
            return Array.isArray(value);
    }
    return false;
}

export function isLegacyTypesenseParityCollection(value: string): value is LegacyTypesenseParityCollection {
    return Object.hasOwn(schemasByCollection, value);
}

export function validateLegacyTypesenseRequiredFields(
    collection: LegacyTypesenseParityCollection,
    document: TypesenseParityDocument,
): TypesenseRequiredFieldFailure[] {
    const schema: CollectionCreateSchema = schemasByCollection[collection]!;
    const fields: CollectionFieldSchema[] = [
        {name: "id", type: "string"},
        ...schema.fields.filter((field) => !field.optional && !field.name.includes(".")),
    ];
    return fields.flatMap((field) => {
        const value = document[field.name];
        if (hasOwn(document, field.name) && matchesType(value, field.type)) {
            return [];
        }
        return [
            {
                field: field.name,
                expectedType: field.type,
                actualType: hasOwn(document, field.name) ? actualType(value) : "absent",
                value,
            },
        ];
    });
}

export function compareLegacyTypesenseDocuments(
    collection: LegacyTypesenseParityCollection,
    cloud: TypesenseParityDocument,
    internal: TypesenseParityDocument,
): LegacyTypesenseDocumentParity {
    const isParts = collection === partsSchema.name || collection === partsVectorizedSchema.name;
    const normalized = isParts
        ? normalizePropertyUnits(cloud, internal)
        : {cloud, internal, accepted: [] as AcceptedRepresentationDifference[]};
    const fieldDifferences = differences(normalized.cloud, normalized.internal);
    const partsMpn = isParts
        ? {
              hasMpnMismatch:
                  hasOwn(cloud, "has_mpn") !== hasOwn(internal, "has_mpn") ||
                  !equal(cloud["has_mpn"], internal["has_mpn"]),
              mpnFieldPresenceMismatch: hasOwn(cloud, "mpn") !== hasOwn(internal, "mpn"),
              mpnValueMismatch:
                  hasOwn(cloud, "mpn") !== hasOwn(internal, "mpn") || !equal(cloud["mpn"], internal["mpn"]),
          }
        : undefined;
    return {
        fieldDifferences,
        acceptedRepresentationDifferences: normalized.accepted,
        requiredFieldFailures: {
            cloud: validateLegacyTypesenseRequiredFields(collection, cloud),
            internal: validateLegacyTypesenseRequiredFields(collection, internal),
        },
        partsMpn,
        equal: fieldDifferences.length === 0,
    };
}
